import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import days from 'dayjs';
import Swal from 'sweetalert2';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { AlertService } from '@app/shared/alert.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { isSealedAttach } from '@app/shared/attach/attach-seal';
import { canOpenInline } from '@app/shared/attach/attach-inline';
import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { Attach, C_value } from '@app/shared/_models/common';
import { Sales_order, roundHalfAway } from '@app/shared/_models/sales';
import { C_VALUES_DELIVERY_MODE, C_VALUES_DELIVERY_SLOT, DocType, ObjectType, OrderLineType, sales_order_mode } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';
import { orderLineNamed, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';
import { todayStr, pickedDateStr } from '@app/shared/time';
import { DelRun, DelrunsService } from '@app/logistic/delruns/delruns.service';
import { RentalDirectionComponent } from '@app/shared/rental-direction/rental-direction.component';
import { ATTACH_FLAG_SCAN_BL, DeliveryLineQty, PassageExtra, TourReturnDelivery, TourReturnService } from './tour-return.service';

type Show = 'todo' | 'done' | 'all';

// Pièce du BL : commande, ou location (BL d'un dossier F3 et BL de vente de son passage, livrés ensemble par le 703).
export type Kind = 'sale' | 'rental';

// L'annulation ne supprime pas les BL d'une commande : sans ce filtre, « Journée » les passerait à livré.
const CLOSED_ORDER_STATES: ReadonlySet<string> = new Set([SalesOrderState.CANCELED, SalesOrderState.REFUSED]);

// Commande validée, ni annulée ni reprise : ses BL se corrigent et son reste s'annule (miroir de lines_refusal côté serveur).
const LINES_OPEN_STATES: ReadonlySet<string> = new Set([SalesOrderState.PREPARATION, SalesOrderState.DELIVERED, SalesOrderState.INVOICED, SalesOrderState.PAID]);

// Formats d'un scan de BL : liste blanche, rien d'exécutable (svg, html…) n'entre par le glisser-déposer.
const SCAN_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/tiff', 'image/heic'];

// Ligne de la liste : le BL + ce qu'on en déduit une fois (créneau effectif, tournée, pièces).
interface ReturnRow {
	d: TourReturnDelivery;
	day: number;							// 0 = sans créneau, 1 = Matin, 2 = Après-midi
	run: DelRun | null;
	docs: Attach[];						// pièces montrables : tout sauf le mémo texte
	proof: 'scan' | 'signed' | null;
	uploading: number;				// envois en cours sur cette ligne
	rental: RentalMark | null;
}

// BL de location : lignes posées et reprises (−1, JP 29/10), ou BL de vente du passage d'un BL de dossier (head).
export interface RentalMark {
	posed: number;
	taken: number;
	head: number | null;
}

// Ligne de la commande du BL cliqué : ce qu'en portent ce BL, les autres BL et les bons d'annulation.
export interface DetailLine {
	id: number;								// id de la ligne de commande
	seq: number;
	product?: number;
	name: string;							// intitulé du contrat de location pour une ligne louée, comme le BL imprimé ; l'article sinon
	article: string | null;		// l'article sous l'intitulé du contrat ; null sans contrat
	packaging: string;
	ordered: number;
	saved: number;						// qté de CE BL en base ; négative = reprise du matériel loué (−1 sur sa ligne, JP 29/10)
	qty: number | null;				// qté saisie pour ce BL
	others: number;						// posé par les autres BL, livrés ou à livrer (une reprise n'y entre pas)
	cancelled: number;				// bons d'annulation déjà posés
	cancel: number | null;		// qté à annuler, saisie
	legacy?: boolean;					// ancienne ligne RENTAL_RETURN : jamais à livrer
}

interface Detail {
	deliveryId: number;
	orderId: number;
	loading: boolean;
	error: string;
	order: Sales_order | null;
	lines: DetailLine[];
	hiddenRal: number;				// reste des lignes d'un dossier de location absentes de ce BL (non montrées, #55)
	cancelling: boolean;
}

// Au centième : la forme de sales_order_l.qty (NUMERIC 11,2) que le serveur exige aussi ; le signe se borne ligne par ligne (minOnBl, maxOnBl).
function isQty(v: number | null): v is number {
	return v !== null && Number.isFinite(v) && roundHalfAway(v, 2) === v;
}

@Component({
	selector: 'app-tour-return',
	standalone: true,
	templateUrl: './tour-return.component.html',
	styleUrls: ['./tour-return.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MenuCommonComponent, SelectDateComponent, RouterLink, DatePipe, FormsModule, MatButtonModule, MatButtonToggleModule, MatCheckboxModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, RentalDirectionComponent],
})
export class TourReturnComponent implements OnInit, OnDestroy {

	private tourReturnService = inject(TourReturnService);
	private delrunsService = inject(DelrunsService);
	private valuesService = inject(ValuesService);
	private attachService = inject(AttachService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private spinner = inject(SpinnerService);
	private subscriptions = new Subscription();

	public date: Date = new Date();
	public show: Show = 'todo';
	public loading = false;
	public rows: ReturnRow[] = [];
	public selected = new Set<number>();
	public focused: ReturnRow | null = null;
	public detail: Detail | null = null;
	public saving = false;
	public dragOverId: number | null = null;
	public thumbs = new Map<number, string>();		// id attach -> object URL de la vignette
	public modeValues: C_value[] = [];						// types de livraison (c_values 10), modes 1 à 4
	public modes: number[] = [];									// types affichés : bascules multi-sélection
	public kinds: Kind[] = ['sale', 'rental'];		// pièces affichées : commandes, locations
	private slots: C_value[] = [];
	private loadToken = 0;
	private detailToken = 0;

	ngOnInit(): void {
		const values = (id: number) => this.valuesService.getValues$(id).pipe(take(1), catchError(() => of([] as C_value[])));
		this.subscriptions.add(forkJoin({ slots: values(C_VALUES_DELIVERY_SLOT), modes: values(C_VALUES_DELIVERY_MODE) }).subscribe(({ slots, modes }) => {
			this.slots = slots ?? [];
			// Les modes négatifs sont réservés (bon d'annulation) ; c_values absent : les libellés du code.
			const known = (modes ?? []).filter((m: C_value) => (m.id ?? 0) >= 1);
			this.modeValues = known.length ? known : [...sales_order_mode.keys()].filter((id) => id >= 1).map((id) => ({ id, name: sales_order_mode.get(id) }));
			this.modes = this.modeValues.map((m) => m.id!);
			this.load();
		}));
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.thumbs.forEach((url) => URL.revokeObjectURL(url));
	}

	// ============================================================ Chargement

	load(): void {
		const token = ++this.loadToken;
		const focusedId = this.focused?.d.id ?? null;
		this.loading = true;
		this.rows = [];
		this.focused = null;
		this.subscriptions.add(forkJoin({
			deliveries: this.tourReturnService.getDeliveriesOfDay$(this.date),
			runs: this.delrunsService.getDelRunsByDate(this.date).pipe(catchError(() => of([] as DelRun[]))),
		}).subscribe({
			next: ({ deliveries, runs }) => {
				if (token !== this.loadToken) return;
				const runsById = new Map((runs ?? []).map((r) => [r.id, r] as [number, DelRun]));
				// Vrais BL (modes 1 à 4) de commandes ni annulées ni refusées ; ni bon d'annulation, ni BL de comptoir (F1, réalisé et facturé au paiement).
				this.rows = (deliveries ?? [])
					.filter((d) => d.id > 0 && d.mode >= 1 && !CLOSED_ORDER_STATES.has(d.lnk_order?.state ?? '') && d.lnk_order?.doc_type !== DocType.TICKET)
					.map((d) => this.toRow(d, runsById))
					.sort((a, b) => this.sortKey(a) - this.sortKey(b) || (a.d.seq_run ?? 0) - (b.d.seq_run ?? 0) || a.d.id - b.d.id);
				this.pruneSelection();
				this.loading = false;
				const again = focusedId !== null ? this.rows.find((r) => r.d.id === focusedId) : undefined;
				if (again) this.focus(again, true);
				else this.detail = null;
			},
			error: (e) => {
				if (token !== this.loadToken) return;
				this.loading = false;
				console.error(e);
				this.alertService.error('ERREUR au chargement des bons de livraison : ' + (e?.error?.message ?? e?.message ?? ''));
			},
		}));
	}

	private toRow(d: TourReturnDelivery, runsById: Map<number, DelRun>): ReturnRow {
		const run = d.id_del_run ? runsById.get(d.id_del_run) ?? null : null;
		// Le créneau de la tournée prime : c'est avec elle que le BL est parti ; sinon la demande du client.
		const day = run?.slot === 1 || run?.slot === 2 ? run.slot : DelrunsService.slotDayOf(d.askslot, this.slots);
		const row: ReturnRow = { d, day, run, docs: [], proof: null, uploading: 0, rental: TourReturnComponent.rentalMark(d) };
		this.refreshDocs(row);
		return row;
	}

	// Une ligne négative n'existe que sur un matériel loué (garde serveur) : le signe suffit à dire livraison ou reprise.
	static rentalMark(d: TourReturnDelivery): RentalMark | null {
		const head = d.lnk_order?.doc_type === DocType.LOCATION ? null : d.id_planning || null;
		if (d.lnk_order?.doc_type !== DocType.LOCATION && !head) return null;
		const qtys = (d.lnk_deliveryl ?? []).map((l) => Number(l.qty));
		return { posed: qtys.filter((q) => q > 0).length, taken: qtys.filter((q) => q < 0).length, head };
	}

	private refreshDocs(row: ReturnRow): void {
		row.docs = (row.d.lnk_attachs ?? []).filter((a) => !this.flags(a).includes('is_default_memo'));
		row.proof = row.docs.some((a) => this.flags(a).includes(ATTACH_FLAG_SCAN_BL)) ? 'scan'
			: row.docs.some((a) => this.flags(a).includes('signed_bl')) ? 'signed' : null;
	}

	private flags(a: Attach): string[] {
		return Array.isArray(a?.data?.['flags']) ? a.data!['flags'] : [];
	}

	// Sans créneau d'abord (emportée, retrait, ASAP), puis Matin, puis Après-midi ; dans un créneau, par tournée.
	private sortKey(r: ReturnRow): number {
		return r.day * 1000 + (r.run?.seq ?? 999);
	}

	// Seuls les BL à traiter ET affichés restent sélectionnés : un type masqué ne part jamais à livré sans avoir été vu.
	private pruneSelection(): void {
		const eligible = new Set(this.modeRows.filter((r) => !this.isDone(r)).map((r) => r.d.id));
		this.selected.forEach((id) => { if (!eligible.has(id)) this.selected.delete(id); });
	}

	dateChange(date: any): void {
		// Champ vidé ou illisible, ou saisie gardée : on garde le jour, rendu au sélecteur par une nouvelle référence.
		if (!pickedDateStr(date)) { this.date = new Date(this.date); return; }
		this.afterDiscard(() => {
			this.date = date;
			this.selected.clear();
			this.load();
		}, () => this.date = new Date(this.date));
	}

	reload(): void {
		this.afterDiscard(() => this.load());
	}

	// Les filtres ne ferment pas le panneau : le BL cliqué et sa saisie restent, même masqués de la liste.
	setShow(s: Show): void {
		this.show = s;
	}

	setModes(modes: number[]): void {
		this.modes = modes ?? [];
		this.pruneSelection();
	}

	setKinds(kinds: Kind[]): void {
		this.kinds = kinds ?? [];
		this.pruneSelection();
	}

	// ============================================================ Affichage

	kindOf(r: ReturnRow): Kind {
		return r.rental ? 'rental' : 'sale';
	}

	// Filtres des types et des pièces (bascules), avant celui de l'avancement.
	get modeRows(): ReturnRow[] {
		return this.rows.filter((r) => this.modes.includes(r.d.mode) && this.kinds.includes(this.kindOf(r)));
	}

	get visibleRows(): ReturnRow[] {
		const rows = this.modeRows;
		if (this.show === 'all') return rows;
		return rows.filter((r) => (this.show === 'done') === this.isDone(r));
	}

	isDone(r: ReturnRow): boolean {
		return !!r.d.realdatdel;
	}

	private matchesShow(r: ReturnRow): boolean {
		return this.show === 'all' || (this.show === 'done') === this.isDone(r);
	}

	get countTodo(): number { return this.modeRows.filter((r) => !this.isDone(r)).length; }
	get countDone(): number { return this.modeRows.length - this.countTodo; }
	get countMissingScan(): number { return this.modeRows.filter((r) => !this.isDone(r) && !r.proof).length; }

	// BL de ce type dans l'avancement choisi (À traiter, Livrés, Tous) et les pièces affichées : ce qu'ajouterait la bascule.
	countMode(mode: number): number {
		return this.rows.filter((r) => r.d.mode === mode && this.kinds.includes(this.kindOf(r)) && this.matchesShow(r)).length;
	}

	countKind(kind: Kind): number {
		return this.rows.filter((r) => this.kindOf(r) === kind && this.modes.includes(r.d.mode) && this.matchesShow(r)).length;
	}

	// BL qui ne fait que reprendre du matériel loué : « À reprendre », puis « Repris ».
	isReprise(r: ReturnRow): boolean {
		return !!r.rental && !r.rental.head && r.rental.taken > 0 && r.rental.posed === 0;
	}

	rentalTip(r: ReturnRow): string {
		const k = r.rental;
		if (!k) return '';
		if (k.head) return `Vente du passage du BL ${k.head} (dossier de location) : déclarée livrée avec lui`;
		const parts = [k.posed ? `${k.posed} ligne${k.posed > 1 ? 's' : ''} à livrer` : '', k.taken ? `${k.taken} matériel${k.taken > 1 ? 's' : ''} à reprendre` : ''].filter(Boolean);
		return `Dossier de location ${r.d.id_order}` + (parts.length ? ' : ' + parts.join(', ') : '');
	}

	modeLabel(mode: number): string {
		return this.modeValues.find((m) => m.id === mode)?.name ?? sales_order_mode.get(mode) ?? '';
	}

	// Étiquette de colonne : « Livraison dès que possible » → « Dès que possible », le reste tel quel.
	modeShort(mode: number): string {
		const name = this.modeLabel(mode).replace(/^Livraison\s+/i, '');
		return name.charAt(0).toUpperCase() + name.slice(1);
	}

	dayLabel(day: number): string {
		return day === 1 ? 'Matin' : day === 2 ? 'Après-midi' : '—';
	}

	runLabel(r: ReturnRow): string {
		if (!r.run) return 'Hors tournée';
		return 'T' + r.run.seq + (r.d.seq_run ? ' · #' + r.d.seq_run : '');
	}

	customerLabel(r: ReturnRow): string {
		const c = r.d.lnk_order?.lnk_customer;
		return ((c?.name ?? '') + ' ' + (c?.firstname ?? '')).trim() || '—';
	}

	addressLabel(r: ReturnRow): string {
		const p = r.d.lnk_order?.lnk_partner_deliv ?? r.d.lnk_order?.lnk_customer;
		return [p?.street1, [p?.zip, p?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
	}

	cityLabel(r: ReturnRow): string {
		const p = r.d.lnk_order?.lnk_partner_deliv ?? r.d.lnk_order?.lnk_customer;
		return p?.city ?? '';
	}

	isScan(a: Attach): boolean {
		return this.flags(a).includes(ATTACH_FLAG_SCAN_BL);
	}

	// Jour futur : le serveur date le retour de l'instant du clic, jamais d'un jour à venir.
	get isFuture(): boolean {
		return (pickedDateStr(this.date) ?? '') > todayStr();
	}

	// ============================================================ Sélection

	toggle(r: ReturnRow, checked: boolean): void {
		if (this.isDone(r)) return;
		if (checked) this.selected.add(r.d.id); else this.selected.delete(r.d.id);
	}

	// null = toute la journée ; 1|2 = ajoute le créneau à la sélection (Matin ET/OU Après-midi) ; types affichés seulement.
	selectSlot(day: number | null): void {
		this.slotRows(day).forEach((r) => this.selected.add(r.d.id));
	}

	countSlot(day: number | null): number {
		return this.slotRows(day).length;
	}

	isSlotFullySelected(day: number | null): boolean {
		const ids = this.slotRows(day).map((r) => r.d.id);
		return ids.length > 0 && ids.every((id) => this.selected.has(id));
	}

	private slotRows(day: number | null): ReturnRow[] {
		return this.modeRows.filter((r) => !this.isDone(r) && (day === null || r.day === day));
	}

	clearSelection(): void {
		this.selected.clear();
	}

	get allVisibleSelected(): boolean {
		const todo = this.visibleRows.filter((r) => !this.isDone(r));
		return todo.length > 0 && todo.every((r) => this.selected.has(r.d.id));
	}

	toggleAllVisible(checked: boolean): void {
		this.visibleRows.filter((r) => !this.isDone(r)).forEach((r) => checked ? this.selected.add(r.d.id) : this.selected.delete(r.d.id));
	}

	// again = même BL rechargé (après un enregistrement, un passage à livré) : pas de question, les lignes se relisent.
	focus(r: ReturnRow, again = false): void {
		if (!again && this.focused === r) return;
		const go = () => {
			this.focused = r;
			this.loadThumbs(r);
			this.loadDetail(r);
		};
		if (again) { go(); return; }
		this.afterDiscard(go);
	}

	// ============================================================ Passage à « livré »

	markDelivered(): void {
		const ids = [...this.selected];
		if (!ids.length) return;
		if (this.isDirty) {
			this.alertService.error('Enregistrez ou rétablissez d\'abord les quantités du BL ' + this.detail?.deliveryId + ' : un BL livré ne se modifie plus.');
			return;
		}
		const withoutScan = this.rows.filter((r) => this.selected.has(r.d.id) && !r.proof).length;
		const dayStr = days(this.date).format('DD/MM/YYYY');
		const when = this.isFuture ? `<b>maintenant</b> (le ${dayStr} est à venir)` : `le <b>${dayStr}</b>`;
		// Un BL d'un passage de location livre aussi l'autre moitié du passage (703) : lue avant la confirmation, annoncée, jamais livrée à l'insu.
		this.subscriptions.add(this.tourReturnService.getPassageExtra$(ids).pipe(this.spinner.block('Lecture des passages de location…')).subscribe({
			next: (extra: PassageExtra[]) => this.confirmDelivered(ids, extra ?? [], when, withoutScan),
			error: (e) => this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? '')),
		}));
	}

	private confirmDelivered(ids: number[], extra: PassageExtra[], when: string, withoutScan: number): void {
		const listed = extra.map((x) => `BL ${x.id} (${x.doc_type === 'LOCATION' ? 'dossier de location' : 'commande de vente'} ${x.id_order})`).join(', ');
		Swal.fire({
			title: 'Déclarer livrés ?',
			html: `<b>${ids.length + extra.length}</b> BL seront déclarés livrés ${when}. Un BL livré ne se modifie plus.`
				+ (extra.length ? `<br><br>⚠ Location : ${extra.length > 1 ? 'les' : 'le'} <b>${listed}</b> ${extra.length > 1 ? 'partent' : 'part'} avec un BL coché (même passage) et ${extra.length > 1 ? 'seront livrés' : 'sera livré'} aussi.` : '')
				+ (withoutScan ? `<br><br>⚠ <b>${withoutScan}</b> d'entre eux n'ont pas de scan du BL signé.` : ''),
			icon: withoutScan || extra.length ? 'warning' : 'question',
			showCancelButton: true,
			confirmButtonText: 'Déclarer livrés',
			cancelButtonText: 'Annuler',
		}).then((res) => {
			if (!res.isConfirmed) return;
			this.subscriptions.add(this.tourReturnService.postDeliveryReturn$(ids, this.date).pipe(this.spinner.block('Passage des BL à « livré »…')).subscribe({
				next: (response: any) => {
					if (response?.success === false) {
						this.alertService.error(response.msg || 'Passage à « livré » refusé');
						return;
					}
					const done: number[] = response?.data?.done ?? [];
					const skipped: number[] = response?.data?.skipped ?? [];
					this.alertService.success(done.length + ' BL déclaré(s) livré(s)' + (skipped.length ? ' — ' + skipped.length + ' déjà réalisé(s) : ' + skipped.join(', ') : ''));
					this.selected.clear();
					this.load();
				},
				error: (e) => {
					console.error(e);
					this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? ''));
				},
			}));
		});
	}

	// ============================================================ Lignes du BL et reste à livrer

	// Même BL relu (après un enregistrement) : ses lignes restent affichées pendant la requête.
	private loadDetail(r: ReturnRow): void {
		const token = ++this.detailToken;
		if (this.detail?.deliveryId === r.d.id) Object.assign(this.detail, { loading: true, error: '', cancelling: false });
		else this.detail = { deliveryId: r.d.id, orderId: r.d.id_order, loading: true, error: '', order: null, lines: [], hiddenRal: 0, cancelling: false };
		const rental = r.d.lnk_order?.doc_type === DocType.LOCATION;
		this.subscriptions.add(this.tourReturnService.getOrderWithDeliveries$(r.d.id_order, rental ? r.d.id : undefined).subscribe({
			next: (orders) => {
				if (token !== this.detailToken || !this.detail) return;
				const order = orders?.[0] ?? null;
				this.detail.loading = false;
				if (!order) { this.detail.error = 'Commande ' + r.d.id_order + ' introuvable.'; return; }
				this.detail.order = order;
				const all = TourReturnComponent.buildDetailLines(order, r.d.id);
				// Audit 31/10 #55 : un BL de dossier ne montre que ses lignes ; le reste du dossier compte encore dans le R.A.L.
				const shown = rental ? all.filter((l) => l.saved !== 0) : all;
				this.detail.lines = shown;
				this.detail.hiddenRal = rental ? TourReturnComponent.orderRal(all.filter((l) => l.saved === 0)) : 0;
				this.setOrderRal(r.d.id_order, TourReturnComponent.orderRal(all));
			},
			error: (e) => {
				if (token !== this.detailToken || !this.detail) return;
				console.error(e);
				this.detail.loading = false;
				this.detail.error = 'ERREUR au chargement de la commande : ' + (e?.error?.message ?? e?.message ?? '');
			},
		}));
	}

	// Lignes de la commande et leur ventilation : ce BL, les autres BL (modes ≥ 0), les bons d'annulation (modes < 0). Pure => testable.
	static buildDetailLines(order: Sales_order, deliveryId: number): DetailLine[] {
		const sums = new Map<number, { saved: number; others: number; cancelled: number }>();
		for (const d of order.lnk_deliveries ?? []) {
			for (const l of d.lnk_deliveryl ?? []) {
				const s = sums.get(l.id_order_l) ?? { saved: 0, others: 0, cancelled: 0 };
				const q = Number(l.qty) || 0;
				if (d.id === deliveryId) s.saved += q;
				else if (d.mode < 0) s.cancelled += q;
				else if (q > 0) s.others += q;
				sums.set(l.id_order_l, s);
			}
		}
		return [...(order.lines ?? [])]
			.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
			.map((l) => {
				const s = sums.get(l.id) ?? { saved: 0, others: 0, cancelled: 0 };
				const saved = roundHalfAway(s.saved, 2);
				const named = orderLineNamed(l);
				return {
					id: l.id, seq: l.seq ?? 0, product: l.product, name: rentalTitle(named) ?? '', article: rentalSubtitle(named), packaging: l.packaging_name ?? '',
					ordered: Number(l.qty) || 0, saved, qty: saved, others: roundHalfAway(s.others, 2), cancelled: roundHalfAway(s.cancelled, 2), cancel: null,
					legacy: l.typ === OrderLineType.RENTAL_RETURN,
				};
			});
	}

	// Reste de la ligne avec la saisie en cours sur ce BL (l'enregistré tant qu'elle est hors bornes) ; négatif pour une ligne de retour.
	// Seul le posé compte : une reprise (−1) ne remet rien à livrer ; une ancienne ligne RENTAL_RETURN n'a rien à livrer.
	static lineRal(l: DetailLine): number {
		if (l.legacy) return 0;
		const onBl = isQty(l.qty) && l.qty <= TourReturnComponent.maxOnBl(l) && l.qty >= TourReturnComponent.minOnBl(l) ? l.qty : l.saved;
		return roundHalfAway(l.ordered - Math.max(onBl, 0) - l.others - l.cancelled, 2);
	}

	// Plafond de ce BL : commandé moins le posé des autres bons (règle du serveur, event 704) ; 0 pour une ancienne ligne RENTAL_RETURN.
	static maxOnBl(l: DetailLine): number {
		return l.legacy ? 0 : Math.max(0, roundHalfAway(l.ordered - l.others - l.cancelled, 2));
	}

	// Plancher : une reprise enregistrée (−1) se ramène vers 0 si le matériel n'est pas revenu ; elle ne se crée pas ici (Récupérer du dossier).
	static minOnBl(l: DetailLine): number {
		return Math.min(0, l.saved);
	}

	// Reste à livrer de la commande = Σ des restes positifs, comme GET /deliveries?o=ral.
	static orderRal(lines: DetailLine[]): number {
		return roundHalfAway(lines.reduce((sum, l) => sum + Math.max(0, TourReturnComponent.lineRal(l)), 0), 2);
	}

	lineRal(l: DetailLine): number { return TourReturnComponent.lineRal(l); }
	maxOnBl(l: DetailLine): number { return TourReturnComponent.maxOnBl(l); }
	minOnBl(l: DetailLine): number { return TourReturnComponent.minOnBl(l); }
	get detailRal(): number { return this.detail ? roundHalfAway(TourReturnComponent.orderRal(this.detail.lines) + this.detail.hiddenRal, 2) : 0; }

	// Soldée : plus rien à livrer ET plus aucun bon ouvert (tout est livré ou annulé).
	get isSettled(): boolean {
		const ds = this.detail?.order?.lnk_deliveries ?? [];
		return !!this.detail?.order && this.detailRal === 0 && !this.isDirty && ds.every((d) => !!d.realdatdel);
	}

	get cancelCount(): number {
		return new Set((this.detail?.order?.lnk_deliveries ?? []).filter((d) => d.mode < 0).map((d) => d.id)).size;
	}

	private get orderState(): string {
		return String(this.detail?.order?.state ?? '').toUpperCase();
	}

	// BL du panneau encore modifiable : vrai BL, ni livré ni facturé, commande validée ni annulée ni reprise.
	get canEditLines(): boolean {
		const r = this.focused;
		if (!r || !this.detail?.order || this.detail.deliveryId !== r.d.id) return false;
		return r.d.mode >= 1 && !r.d.realdatdel && !r.d.datinvoicing && LINES_OPEN_STATES.has(this.orderState);
	}

	get lockReason(): string {
		const r = this.focused;
		if (!r || !this.detail?.order) return '';
		if (r.d.realdatdel) return 'BL livré : ses quantités sont figées.';
		if (r.d.datinvoicing) return 'BL facturé : ses quantités sont scellées dans la facture.';
		if (!LINES_OPEN_STATES.has(this.orderState)) return 'Commande à l\'état ' + this.orderState + ' : ses livraisons ne se modifient pas.';
		return '';
	}

	isQtyBad(l: DetailLine): boolean {
		return !isQty(l.qty) || l.qty > TourReturnComponent.maxOnBl(l) || l.qty < TourReturnComponent.minOnBl(l);
	}

	isCancelBad(l: DetailLine): boolean {
		return !isQty(l.cancel) || l.cancel > Math.max(0, TourReturnComponent.lineRal(l));
	}

	get isDirty(): boolean {
		return !!this.detail?.lines.some((l) => l.qty !== l.saved);
	}

	get hasBadQty(): boolean {
		return !!this.detail?.lines.some((l) => this.isQtyBad(l));
	}

	// Le reste s'annule sur une commande F2 validée, sur les quantités ENREGISTRÉES : jamais par-dessus une saisie en cours.
	get canCancelRemaining(): boolean {
		const d = this.detail;
		return !!d?.order && !d.loading && d.order.doc_type === DocType.FACTURE && LINES_OPEN_STATES.has(this.orderState) && !this.isDirty && this.detailRal > 0;
	}

	get cancelTooltip(): string {
		const d = this.detail;
		if (!d?.order) return '';
		if (d.order.doc_type !== DocType.FACTURE) return 'Seul le reste d\'une commande livrable (F2) s\'annule.';
		if (!LINES_OPEN_STATES.has(this.orderState)) return 'Commande à l\'état ' + this.orderState + ' : son reste ne s\'annule pas.';
		if (this.isDirty) return 'Enregistrez d\'abord les quantités du BL.';
		if (this.detailRal <= 0) return 'Rien ne reste à livrer sur cette commande.';
		return 'Solder les quantités qui ne seront jamais livrées : un bon d\'annulation, définitif, jamais facturé.';
	}

	resetLines(): void {
		this.detail?.lines.forEach((l) => l.qty = l.saved);
	}

	saveLines(): void {
		const d = this.detail;
		if (!d || !this.canEditLines || !this.isDirty || this.saving) return;
		if (this.hasBadQty) { this.alertService.error('Quantité en erreur : dans les bornes de la ligne, au centième.'); return; }
		// Toute ligne non nulle repart, la reprise (−1) comprise : le serveur remplace toutes les lignes du BL.
		const lines: DeliveryLineQty[] = d.lines.filter((l) => (l.qty ?? 0) !== 0).map((l) => ({ id_order_l: l.id, qty: l.qty! }));
		this.saving = true;
		this.subscriptions.add(this.tourReturnService.postDeliveryLines$(d.deliveryId, lines).pipe(this.spinner.block('Enregistrement des quantités du BL…')).subscribe({
			next: (response: any) => {
				this.saving = false;
				if (response?.success === false) { this.alertService.error(response.msg || 'Quantités refusées'); return; }
				this.alertService.success('BL ' + d.deliveryId + ' enregistré : ' + (response?.data?.nb_lines ?? lines.length) + ' ligne(s)');
				d.lines.forEach((l) => l.saved = l.qty ?? l.saved);
				if (this.focused) this.loadDetail(this.focused);
			},
			error: (e) => {
				this.saving = false;
				console.error(e);
				this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? ''));
			},
		}));
	}

	// Ouvre la saisie : chaque ligne propose d'annuler tout son reste, que l'on réduit si une partie sera encore livrée.
	startCancel(): void {
		const d = this.detail;
		if (!d || !this.canCancelRemaining) return;
		d.lines.forEach((l) => l.cancel = Math.max(0, TourReturnComponent.lineRal(l)));
		d.cancelling = true;
	}

	abortCancel(): void {
		if (!this.detail) return;
		this.detail.cancelling = false;
		this.detail.lines.forEach((l) => l.cancel = null);
	}

	confirmCancel(): void {
		const d = this.detail;
		if (!d?.cancelling || this.saving) return;
		if (d.lines.some((l) => l.cancel !== null && l.cancel !== 0 && this.isCancelBad(l))) {
			this.alertService.error('Quantité à annuler en erreur : entre 0 et le reste de la ligne, au centième.');
			return;
		}
		const lines: DeliveryLineQty[] = d.lines.filter((l) => (l.cancel ?? 0) > 0).map((l) => ({ id_order_l: l.id, qty: l.cancel! }));
		if (!lines.length) { this.alertService.error('Aucune quantité à annuler.'); return; }
		const total = roundHalfAway(lines.reduce((s, l) => s + l.qty, 0), 2);
		Swal.fire({
			title: 'Annuler le reste à livrer ?',
			html: `Commande <b>${d.orderId}</b> : <b>${total}</b> article(s) sur <b>${lines.length}</b> ligne(s) ne seront jamais livrés.`
				+ '<br><br>Le bon d\'annulation est définitif et ne se facture pas.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Générer le bon d\'annulation',
			cancelButtonText: 'Retour',
		}).then((res) => {
			if (!res.isConfirmed) return;
			this.saving = true;
			this.subscriptions.add(this.tourReturnService.postCancelRemaining$(d.orderId, lines).pipe(this.spinner.block('Création du bon d\'annulation…')).subscribe({
				next: (response: any) => {
					this.saving = false;
					if (response?.success === false) { this.alertService.error(response.msg || 'Annulation refusée'); return; }
					this.alertService.success('Bon d\'annulation ' + (response?.data?.delivery_id ?? '') + ' : ' + (response?.data?.nb_lines ?? lines.length) + ' ligne(s) soldée(s)');
					if (this.focused) this.loadDetail(this.focused);
				},
				error: (e) => {
					this.saving = false;
					console.error(e);
					this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? ''));
				},
			}));
		});
	}

	// Toutes les lignes de la liste qui partagent la commande reçoivent son reste relu.
	private setOrderRal(orderId: number, ral: number): void {
		this.rows.filter((r) => r.d.id_order === orderId).forEach((r) => r.d.ral = ral);
	}

	// Saisie de quantités en cours : on demande avant de la perdre (autre BL, autre jour, rechargement) ; sinon on enchaîne sans attendre.
	private afterDiscard(go: () => void, kept?: () => void): void {
		if (!this.isDirty) { go(); return; }
		Swal.fire({
			title: 'Quantités non enregistrées',
			text: 'Les quantités saisies sur le BL ' + this.detail?.deliveryId + ' seront perdues.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Abandonner la saisie',
			cancelButtonText: 'Rester',
		}).then((res) => res.isConfirmed ? go() : kept?.());
	}

	// ============================================================ Scan du BL signé

	onFilesPicked(r: ReturnRow, input: HTMLInputElement): void {
		this.upload(r, Array.from(input.files ?? []));
		input.value = '';		// re-choisir le même fichier doit relancer l'envoi
	}

	onDragOver(r: ReturnRow, ev: DragEvent): void {
		if (!ev.dataTransfer?.types?.includes('Files')) return;
		ev.preventDefault();
		this.dragOverId = r.d.id;
	}

	onDragLeave(r: ReturnRow): void {
		if (this.dragOverId === r.d.id) this.dragOverId = null;
	}

	onDrop(r: ReturnRow, ev: DragEvent): void {
		ev.preventDefault();
		this.dragOverId = null;
		this.upload(r, Array.from(ev.dataTransfer?.files ?? []));
	}

	private upload(r: ReturnRow, files: File[]): void {
		const ok = files.filter((f) => SCAN_TYPES.includes(f.type));
		if (ok.length < files.length) this.alertService.error('Seuls les PDF et les images JPEG, PNG, WebP, TIFF ou HEIC sont acceptés comme scan de BL.');
		ok.forEach((file) => {
			r.uploading++;
			this.subscriptions.add(this.attachService.postAttach$(file, { flags: [ATTACH_FLAG_SCAN_BL] }, ObjectType.DELIVERY, r.d.id).subscribe({
				next: (response: any) => {
					r.uploading--;
					const id = response?.data?.id;
					if (!id) { this.alertService.error('Scan non enregistré : ' + (response?.msg ?? 'réponse sans id')); return; }
					const attach = new Attach({ id, name: file.name, contenttype: file.type, userid: this.loginService.loggeduser?.id ?? 0, objecttyp: ObjectType.DELIVERY, objectid: r.d.id, data: { flags: [ATTACH_FLAG_SCAN_BL] } });
					r.d.lnk_attachs = [...(r.d.lnk_attachs ?? []), attach];
					this.refreshDocs(r);
					if (this.focused === r) this.loadThumbs(r);
				},
				error: (e) => {
					r.uploading--;
					console.error(e);
					this.alertService.error('ERREUR à l\'envoi de « ' + file.name + ' » : ' + (e?.error?.message ?? e?.message ?? ''));
				},
			}));
		});
	}

	openDoc(a: Attach): void {
		if (!a.id) return;
		if (canOpenInline(a.contenttype)) this.attachService.openInNewTab(a.id);
		else this.download(a);
	}

	// Type qu'un onglet exécuterait (svg, html…) : téléchargé en octet-stream, jamais ouvert dans l'origine du front.
	private download(a: Attach): void {
		this.subscriptions.add(this.attachService.loadImage$(a.id!).pipe(take(1)).subscribe({
			next: (blob: Blob) => {
				const url = URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
				const link = document.createElement('a');
				link.href = url;
				link.download = a.name || 'document-' + a.id;
				link.click();
				setTimeout(() => URL.revokeObjectURL(url), 1000);
			},
			error: (e) => this.alertService.error('ERREUR au téléchargement de « ' + (a.name ?? a.id) + ' » : ' + (e?.error?.message ?? e?.message ?? '')),
		}));
	}

	canDelete(a: Attach): boolean {
		return this.isScan(a) && !isSealedAttach(a);
	}

	deleteDoc(r: ReturnRow, a: Attach): void {
		Swal.fire({ title: 'Retirer ce scan ?', text: a.name ?? '', icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler' }).then((res) => {
			if (!res.isConfirmed) return;
			this.subscriptions.add(this.attachService.suppressImage(a).subscribe({
				next: (response: any) => {
					if (response?.success === false) { this.alertService.error(response.msg || 'Suppression refusée'); return; }
					r.d.lnk_attachs = (r.d.lnk_attachs ?? []).filter((x) => x.id !== a.id);
					this.refreshDocs(r);
				},
				error: (e) => this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.message ?? '')),
			}));
		});
	}

	private loadThumbs(r: ReturnRow): void {
		r.docs.filter((a) => a.id && !this.thumbs.has(a.id)).forEach((a) => {
			this.thumbs.set(a.id!, '');		// réservé : pas deux requêtes pour la même vignette
			this.subscriptions.add(this.attachService.loadThumbnailImage$(a).pipe(take(1), catchError(() => of(null))).subscribe((blob: any) => {
				if (blob) this.thumbs.set(a.id!, URL.createObjectURL(blob));
			}));
		});
	}
}
